<template>
    <div class="page-body">
      <div class="container-fluid">
        <div class="edit-profile">
          <div class="row">
            <div class="col-12 col-xl-12">
              <div class="card">
                <div class="card-header">
                  <div class="text-neutral-500 text-xl font-medium font-['Poppins'] leading-normal">
                    {{ planId && planId !== '' ? "Editar Plan" : "Crear Plan" }}
                  </div>
                </div>
                <div class="card-body" style="margin-top: 20px;">
                  <form @submit.prevent="submitForm">
                    <div class="flex flex-col md:flex-row gap-4 md:gap-8">
                      <!-- ID y Nombre -->
                      <div class="flex flex-col gap-4 w-full md:w-[300px]">
                        <div class="w-full h-[72px] flex-col justify-start items-start gap-1.5 inline-flex">
                          <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Nombre</label>
                          <input 
                            type="text" 
                            v-model="form.name"
                            @input="validateField('name')"
                            class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                            placeholder="Nombre del plan" 
                          />
                          <!-- Alerta para nombre -->
                          <div v-if="showNameAlert" class="required-alert">
                            <div class="yellow-box">!</div>
                            <span>Completa este campo</span>
                          </div>
                        </div>
                        <div class="w-full h-[72px] flex-col justify-start items-start gap-1.5 inline-flex">
                          <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Precio</label>
                          <input 
                            type="text"
                            :value="displayedPrice"
                            @input="updatePrice($event.target.value)"
                            @blur="formatPriceOnBlur"
                            class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                            placeholder="Precio del Plan" 
                            required
                          />
                          <!-- Alerta para precio -->
                          <div v-if="showPriceAlert" class="required-alert">
                            <div class="yellow-box">!</div>
                            <span>Completa este campo</span>
                          </div>
                        </div>
                      </div>
                      <!-- Descripción -->
                      <div class="w-full md:w-[716px] h-[168px] flex-col justify-start items-start gap-1.5 inline-flex">
                        <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Descripción</label>
                        <textarea 
                          v-model="form.description"
                          @input="validateField('description')"
                          class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                          placeholder="Descripción del plan" 
                          required
                        ></textarea>
                      </div>
                    </div>
                    <!-- Botón -->
                    <div class="form-footer flex sm:flex-row mt-4">
                      
                      <!-- Botón de Cancelar -->
                      <button 
                        type="button" 
                        class="custom-cancel-btn"
                        @click="$router.push('/catalogs/planes')">
                        <span class="custom-cancel-btn-text">Cancelar</span>
                      </button>
                      <button type="submit" class="custom-create-btn">
                        <span class="custom-create-btn-text">Guardar</span>
                      </button>
                    </div>
                  </form>
                </div>
                <!-- Mensaje de éxito -->
                <div v-if="successMessage" class="user-alert">
                  <div class="user-alert-content">
                    <div class="user-alert-text">
                      {{ successMessage }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from "axios";
  import { ref, computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import { useRoute } from "vue-router";
  
  export default {
    setup() {
      const store = useStore();
      const session = computed(() => store.getters.getUserSession);
      const route = useRoute();
      const planId = computed(() => route.params.id);  // Obtener el ID del rol desde la URL
      const isSubmitting = ref(false);
      
      // Variables para controlar las alertas
      const showNameAlert = ref(false);
      const showPriceAlert = ref(false);
      const showDescriptionAlert = ref(false);
  
      const form = ref({
        id: "",
        name: "",
        description: "",
        price:""
      });
      const successMessage = ref("");
      const router = useRouter();

      // Función para validar campos
      const validateField = (field) => {
        let isValid = true;
        
        switch(field) {
          case 'name':
            showNameAlert.value = form.value.name.trim() === '';
            isValid = !showNameAlert.value;
            break;
          case 'price':
            showPriceAlert.value = form.value.price.trim() === '';
            isValid = !showPriceAlert.value;
            break;
          case 'description':
            showDescriptionAlert.value = form.value.description.trim() === '';
            isValid = !showDescriptionAlert.value;
            break;
        }
        
        return isValid;
      };

      // Computed para mostrar el precio con formato
      const formattedPrice = computed(() => {
        if (!form.value.price) return "";
        return new Intl.NumberFormat("es-MX", {
          style: "currency",
          currency: "MXN",
          minimumFractionDigits: 2,
        }).format(form.value.price);
      });


      // Propiedad computada para mostrar el valor en el input
      // Computed: Agrega el símbolo $ al valor del precio
      const displayedPrice = computed(() => {
        if (!form.value.price) return ""; // Si está vacío
        return `$${form.value.price}`;
      });

      // Limpia y actualiza el precio al escribir
      const updatePrice = (value) => {
        // Remueve todo lo que no sea número ni punto decimal
        const cleanValue = value.replace(/[^0-9.]/g, "");

        // Solo actualiza el valor si es un número válido
        if (/^\d*\.?\d{0,2}$/.test(cleanValue)) {
          form.value.price = cleanValue;
        }
      };

      // Aplica formato al perder el foco
      const formatPriceOnBlur = () => {
        if (form.value.price === "") return;

        // Convierte el valor en float y aplica formato con 2 decimales
        const numericValue = parseFloat(form.value.price);
        form.value.price = numericValue.toFixed(2);
      };

      // Función para cargar los datos del rol cuando estamos en modo de edición
      const loadRoleData = async () => {
        if (planId.value) {
          try {
            const token = session.value.token_api;
            if (!token) {
              console.error("No se ha encontrado el token de autenticación.");
              return;
            }
  
            const options = {
              headers: {
                Authorization: `Bearer ${token}`
              }
            };
  
            // Hacer la solicitud GET para obtener los datos del rol
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/plan-form/${planId.value}`, options);
            if (response.status === 200) {
              // Asignar los datos al formulario
              form.value.id = response.data.plan.id;
              form.value.name = response.data.plan.name;
              form.value.price = response.data.plan.price;
              form.value.description = response.data.plan.description;
            }
          } catch (error) {
            console.error("Error al cargar los datos del rol:", error);
          }
        }
      };
  
      // Llamar a la función cuando el componente se monta
      onMounted(() => {
        loadRoleData();
      });
  
      // Función para guardar el rol
      const submitForm = async () => {
        // Limpiar alertas primero
        showNameAlert.value = false;
        showPriceAlert.value = false;
        showDescriptionAlert.value = false;
        
        // Validar campos
        const isNameValid = validateField('name');
        const isPriceValid = validateField('Price');
        const isDescriptionValid = validateField('description');
        
        if (!isNameValid || !isPriceValid || !isDescriptionValid || isSubmitting.value) {
          isSubmitting.value = false;
          return;
        }
        
        isSubmitting.value = true;

        try {
          const token = session.value.token_api;
          if (!token) {
            console.error("No se ha encontrado el token de autenticación.");
            isSubmitting.value = false;
            return;
          }
  
          const options = {
            headers: {
              Authorization: `Bearer ${token}`
            }
          };
  
          const endpoint = planId.value
            ? `${process.env.VUE_APP_API_BASE_URL}/plan-form/${planId.value}`
            : `${process.env.VUE_APP_API_BASE_URL}/plan-form`;
            
          const method = planId.value ? "post" : "post";
          const dataToSend = { ...form.value };
          delete dataToSend.id; // Eliminar el ID del cuerpo
  
          // Hacer la solicitud para guardar el rol
          const response = await axios[method](endpoint, dataToSend, options);
  
          if (response.status === 200 || response.status === 201) {
            successMessage.value = "Plan guardado con éxito.";
  
            // Limpiar el formulario si es un nuevo rol
            if (!planId.value) {
              form.value.name = "";
              form.value.description = "";
            }
  
            setTimeout(() => {
              successMessage.value = "";
              router.push({ name: "planes" });
            }, 2000);
          }
        } catch (error) {
          console.error("Error al guardar el rol:", error);
          successMessage.value = "Error al guardar el rol";
          if (error.response) {
            console.error("Error de servidor:", error.response.data);
          }
        } finally {
          isSubmitting.value = false;
        }
      };
  
      return {
        form,
        formattedPrice,
        updatePrice,
        submitForm, // Función existente
        successMessage,
        displayedPrice,
        formatPriceOnBlur,
        isSubmitting,
        showNameAlert,
        showPriceAlert,
        showDescriptionAlert,
        validateField
      };
    }
  };
  </script>

<style>
.disabled-button {
  opacity: 0.5;
  cursor: not-allowed;
}

.required-alert {
  display: flex;
  align-items: center;
  background-color: #fff;
  color: #333;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-top: 5px;
  position: relative;
  max-width: 300px;
  word-wrap: break-word;
}

.required-alert .yellow-box {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background-color: #e59035;
  margin-right: 10px;
  border: 1px solid #e59035;
  border-radius: 4px;
  color: #fff;
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
}

.required-alert::after {
  content: "";
  position: absolute;
  top: -10px;
  left: 20px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #fff;
  z-index: 1;
}

.required-alert::before {
  content: "";
  position: absolute;
  top: -11px;
  left: 19px;
  width: 0;
  height: 0;
  border-left: 11px solid transparent;
  border-right: 11px solid transparent;
  border-bottom: 11px solid #ccc;
  z-index: 0;
}

.custom-create-btn-text {
  color: #FFF;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.custom-cancel-btn {
  display: inline-flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: #4B5563; /* Gris oscuro */
  border: 1px solid #4B5563;
  color: white;
  cursor: pointer;
  margin-right: 10px; /* Espacio entre botones */
  transition: all 0.3s ease;
}

.custom-cancel-btn-text {
  color: #FFF;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.user-alert {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  background-color: #4CAF50;
  color: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.5s, fadeOut 0.5s 2.5s forwards;
}

/* Estilos responsivos */
@media (max-width: 767px) {
  .card-body {
    padding: 15px;
  }
  
  .flex-col-mobile {
    flex-direction: column;
  }
  
  .w-full-mobile {
    width: 100% !important;
  }
  
  .form-footer {
    flex-direction: column;
    gap: 10px;
  }
  
  .custom-cancel-btn, 
  .custom-create-btn {
    width: 100%;
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .md\:w-1\/3,
  .md\:w-2\/3 {
    width: 100% !important;
  }
}

@media (min-width: 768px) {
  .form-footer {
    justify-content: flex-end;
    gap: 10px;
  }
  
  .md\:w-1\/3 {
    width: 33.333333% !important;
  }
  
  .md\:w-2\/3 {
    width: 66.666667% !important;
  }
}
</style>